/**
 * Location Service with Fallback Hierarchy
 * Implements location acquisition with multiple fallback methods:
 * 1. Browser GPS (navigator.geolocation)
 * 2. Telegram WebApp LocationManager API
 * 3. IP-based Geolocation
 * 4. Default Location (-6.1591862, 106.7341688)
 */

import { TelegramWebApp, TelegramLocationData } from '../lib/types';

export interface LocationCoordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
  altitude?: number | null;
  course?: number | null;
  speed?: number | null;
  source?: 'gps' | 'telegram' | 'ip' | 'default';
  timestamp?: Date;
}

export interface LocationResult {
  location: LocationCoordinates | null;
  error: string | null;
  source: 'gps' | 'telegram' | 'ip' | 'default' | null;
  timestamp: Date;
}

export interface LocationServiceOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
  telegramWebApp?: TelegramWebApp | null;
  fallbackToIP?: boolean;
  defaultLocation?: {
    latitude: number;
    longitude: number;
  };
}

class LocationService {
  private defaultLocation = { latitude: -6.1591862, longitude: 106.7341688 };
  private ipGeolocationCache: LocationCoordinates | null = null;
  private ipCacheTimestamp: Date | null = null;
  private readonly IP_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  /**
   * Get current location with fallback hierarchy
   * Priority: GPS → Telegram → IP → Default
   */
  async getCurrentLocation(options: LocationServiceOptions = {}): Promise<LocationResult> {
    const {
      enableHighAccuracy = true,
      timeout = 15000,
      maximumAge = 60000,
      telegramWebApp = null,
      fallbackToIP = true,
      defaultLocation = this.defaultLocation
    } = options;

    console.log('📍 Location Service: Starting location acquisition with fallback hierarchy');

    // Method 1: Browser GPS (highest priority)
    console.log('🛰️ Trying GPS location...');
    try {
      const gpsResult = await this.getGPSLocation({ enableHighAccuracy, timeout, maximumAge });
      if (gpsResult) {
        console.log('✅ GPS location acquired:', gpsResult);
        return {
          location: { ...gpsResult, source: 'gps' },
          error: null,
          source: 'gps',
          timestamp: new Date()
        };
      }
    } catch (error) {
      console.warn('❌ GPS location failed:', error);
    }

    // Method 2: Telegram WebApp LocationManager
    if (telegramWebApp && telegramWebApp.LocationManager) {
      console.log('📱 Trying Telegram WebApp location...');
      try {
        const telegramResult = await this.getTelegramLocation(telegramWebApp);
        if (telegramResult) {
          console.log('✅ Telegram location acquired:', telegramResult);
          return {
            location: { ...telegramResult, source: 'telegram' },
            error: null,
            source: 'telegram',
            timestamp: new Date()
          };
        }
      } catch (error) {
        console.warn('❌ Telegram location failed:', error);
      }
    } else {
      console.log('📱 Telegram WebApp not available, skipping...');
    }

    // Method 3: IP-based Geolocation
    if (fallbackToIP) {
      console.log('🌐 Trying IP-based geolocation...');
      try {
        const ipResult = await this.getIPLocation();
        if (ipResult) {
          console.log('✅ IP location acquired:', ipResult);
          return {
            location: { ...ipResult, source: 'ip' },
            error: null,
            source: 'ip',
            timestamp: new Date()
          };
        }
      } catch (error) {
        console.warn('❌ IP location failed:', error);
      }
    }

    // Method 4: Default location (final fallback)
    console.log('🏢 Using default location:', defaultLocation);
    return {
      location: {
        latitude: defaultLocation.latitude,
        longitude: defaultLocation.longitude,
        source: 'default',
        timestamp: new Date()
      },
      error: 'All location methods failed, using default location',
      source: 'default',
      timestamp: new Date()
    };
  }

  /**
   * Get GPS location using browser geolocation API
   */
  private async getGPSLocation(options: {
    enableHighAccuracy: boolean;
    timeout: number;
    maximumAge: number;
  }): Promise<LocationCoordinates | null> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported by browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            altitude: position.coords.altitude,
            course: (position.coords as any).course,
            speed: (position.coords as any).speed,
            timestamp: new Date(position.timestamp)
          });
        },
        (error) => {
          let errorMessage = 'GPS location failed';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'GPS permission denied';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'GPS position unavailable';
              break;
            case error.TIMEOUT:
              errorMessage = 'GPS location timeout';
              break;
          }
          reject(new Error(errorMessage));
        },
        options
      );
    });
  }

  /**
   * Get location from Telegram WebApp LocationManager API
   */
  private async getTelegramLocation(webApp: TelegramWebApp): Promise<LocationCoordinates | null> {
    return new Promise((resolve, reject) => {
      if (!webApp.LocationManager) {
        reject(new Error('Telegram LocationManager not available'));
        return;
      }

      // Initialize LocationManager if needed
      if (!webApp.LocationManager.isInited) {
        console.log('🔧 Initializing Telegram LocationManager...');
        webApp.LocationManager.init();
      }

      // Check if location is available
      if (!webApp.LocationManager.isLocationAvailable) {
        reject(new Error('Telegram location services not available'));
        return;
      }

      // Request location if not already requested
      if (!webApp.LocationManager.isAccessRequested) {
        console.log('📋 Requesting Telegram location access...');
      }

      // Get location
      webApp.LocationManager.getLocation((locationData: TelegramLocationData | null) => {
        if (locationData) {
          resolve({
            latitude: locationData.latitude,
            longitude: locationData.longitude,
            accuracy: locationData.horizontal_accuracy || undefined,
            altitude: locationData.altitude || undefined,
            course: locationData.course || undefined,
            speed: locationData.speed || undefined,
            timestamp: new Date()
          });
        } else {
          reject(new Error('Telegram location access denied or failed'));
        }
      });
    });
  }

  /**
   * Get location using IP-based geolocation
   */
  private async getIPLocation(): Promise<LocationCoordinates | null> {
    // Check cache first
    if (this.ipGeolocationCache && this.ipCacheTimestamp) {
      const cacheAge = Date.now() - this.ipCacheTimestamp.getTime();
      if (cacheAge < this.IP_CACHE_DURATION) {
        console.log('📦 Using cached IP location');
        return { ...this.ipGeolocationCache, timestamp: this.ipCacheTimestamp };
      }
    }

    try {
      // Try multiple IP geolocation services in order of preference
      const services = [
        {
          name: 'ipapi.co',
          url: 'https://ipapi.co/json/',
          parse: (data: any) => ({
            latitude: data.latitude,
            longitude: data.longitude,
            accuracy: 1000 // IP geolocation is typically less accurate
          })
        },
        {
          name: 'ip-api.com',
          url: 'http://ip-api.com/json/',
          parse: (data: any) => ({
            latitude: data.lat,
            longitude: data.lon,
            accuracy: 1000
          })
        },
        {
          name: 'ipgeolocation.io',
          url: 'https://api.ipgeolocation.io/ipgeo',
          parse: (data: any) => ({
            latitude: parseFloat(data.latitude),
            longitude: parseFloat(data.longitude),
            accuracy: 1000
          })
        }
      ];

      for (const service of services) {
        try {
          console.log(`🌍 Trying ${service.name}...`);
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 5000);

          const response = await fetch(service.url, {
            method: 'GET',
            signal: controller.signal,
            headers: {
              'Accept': 'application/json'
            }
          });

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          const data = await response.json();
          const location = service.parse(data);

          if (location.latitude && location.longitude) {
            console.log(`✅ ${service.name} location acquired`);

            // Cache the result
            this.ipGeolocationCache = location;
            this.ipCacheTimestamp = new Date();

            return location;
          }
        } catch (error) {
          console.warn(`❌ ${service.name} failed:`, error);
          continue;
        }
      }

      throw new Error('All IP geolocation services failed');
    } catch (error) {
      throw new Error(`IP geolocation failed: ${error}`);
    }
  }

  /**
   * Check if location services are available
   */
  async checkLocationAvailability(telegramWebApp?: TelegramWebApp | null): Promise<{
    gps: boolean;
    telegram: boolean;
    ip: boolean;
  }> {
    const results = {
      gps: !!navigator.geolocation,
      telegram: !!(telegramWebApp?.LocationManager?.isLocationAvailable),
      ip: true // IP geolocation is always available as long as we have internet
    };

    console.log('🔍 Location availability check:', results);
    return results;
  }

  /**
   * Request location permissions (for Telegram)
   */
  async requestLocationPermission(telegramWebApp?: TelegramWebApp | null): Promise<boolean> {
    if (telegramWebApp?.LocationManager) {
      try {
        if (!telegramWebApp.LocationManager.isInited) {
          telegramWebApp.LocationManager.init();
        }

        if (!telegramWebApp.LocationManager.isAccessRequested) {
          // The actual request happens when calling getLocation
          console.log('📋 Location permission will be requested on first location attempt');
        }

        return telegramWebApp.LocationManager.isAccessGranted || false;
      } catch (error) {
        console.error('Failed to request Telegram location permission:', error);
        return false;
      }
    }

    return false;
  }

  /**
   * Clear IP geolocation cache
   */
  clearIPCache(): void {
    this.ipGeolocationCache = null;
    this.ipCacheTimestamp = null;
    console.log('🗑️ IP location cache cleared');
  }

  /**
   * Format location for display
   */
  static formatLocation(location: LocationCoordinates): string {
    return `${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}`;
  }

  /**
   * Calculate distance between two coordinates (in meters)
   */
  static calculateDistance(
    loc1: LocationCoordinates,
    loc2: LocationCoordinates
  ): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (loc1.latitude * Math.PI) / 180;
    const φ2 = (loc2.latitude * Math.PI) / 180;
    const Δφ = ((loc2.latitude - loc1.latitude) * Math.PI) / 180;
    const Δλ = ((loc2.longitude - loc1.longitude) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }
}

// Singleton instance
export const locationService = new LocationService();

// Export types and utilities
export type { LocationService };
export default locationService;